import { useQuery, useMutation, keepPreviousData } from '@tanstack/react-query'
import { productsApi } from '../services/products.service'
import { queryClient } from '@/lib/api/queryClient'
import { qk } from '@/lib/api/queryKeys'
import type { ApiProduct, PaginatedProducts, ListProductsQuery, CreateProductInput, UpdateProductInput } from '../model/types'

export function useProducts(filter: ListProductsQuery = {}) {
  const q = useQuery({
    queryKey: qk.products.list(filter),
    queryFn: () => productsApi.list(filter),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  })
  const data: PaginatedProducts = q.data ?? { items: [], total: 0, page: 1, limit: filter.limit ?? 12, totalPages: 1 }
  return { ...q, data }
}

export function useProductBySlug(slug: string | undefined) {
  const q = useQuery({
    queryKey: qk.products.one(slug ?? '__none__'),
    queryFn: () => productsApi.bySlug(slug!),
    enabled: !!slug,
    staleTime: 60_000,
  })
  return q
}

export function useBrands() {
  const q = useQuery({
    queryKey: qk.products.brands,
    queryFn: () => productsApi.brands(),
    staleTime: 5 * 60_000,
  })
  const data: string[] = q.data ?? []
  return { ...q, data }
}

export function useCreateProduct() {
  return useMutation({
    mutationFn: (input: CreateProductInput) => productsApi.create(input),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['products'] }) },
  })
}

export function useUpdateProduct() {
  return useMutation({
    mutationFn: (vars: { id: string; input: UpdateProductInput }) => productsApi.update(vars.id, vars.input),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['products'] }) },
  })
}

export function useDeleteProduct() {
  return useMutation({
    mutationFn: (id: string) => productsApi.remove(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['products'] }) },
  })
}

export type { ApiProduct, ListProductsQuery, PaginatedProducts, CreateProductInput, UpdateProductInput }
